import React, {
  useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, ButtonBase, Chip, Paper, Stack, TextField, Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PageLayout from '../shell/PageLayout';
import { useOperations } from './OperationsProvider';
import HandoverPanel from './HandoverPanel';
import { EmptyState, StaleNotice } from './FeedState';
import { callInQueue, humanMs } from './areas';

const minutesSince = (value) => Math.max(0, Math.round((Date.now() - Date.parse(value || '')) / 60000));

// Every line on this board names a truck, and a truck is a trip somebody is about to act on. So a
// line is a link to that trip rather than a label to read and then go looking for it.
const Line = ({ onClick, primary, secondary }) => (
  <ButtonBase
    onClick={onClick}
    disabled={!onClick}
    sx={{
      width: '100%',
      textAlign: 'left',
      justifyContent: 'space-between',
      gap: 1,
      mt: 1,
      px: 1,
      py: 0.75,
      borderRadius: 1,
      '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>{primary}</Typography>
      {secondary && <Typography variant="caption" color="text.secondary" component="div" noWrap>{secondary}</Typography>}
    </Box>
    {onClick && <ChevronRightIcon fontSize="small" color="action" />}
  </ButtonBase>
);

// A deliberately small shift board: it answers what a desk operator needs before opening a trip.
// Handover notes remain on this device until the server-side shift log is connected, so they never
// pretend to be a shared operational record.
const ShiftDeskPage = () => {
  const { trips, data, updatedAt, error: unavailable } = useOperations();
  const account = useSelector((state) => state.session.user);
  const navigate = useNavigate();
  const [gate, setGate] = useState(() => (account?.postedCheckpointId ? String(account.postedCheckpointId) : ''));
  // A count that names a problem should take you to it. On a phone the card it refers to is below
  // the fold; on a desk it is on screen but the first row still needs finding, so the chip moves
  // the view and puts focus on the first truck in that list — which is then one key from opening.
  const cards = { arrivals: useRef(null), overdue: useRef(null), waiting: useRef(null) };
  const jump = (which) => {
    const card = cards[which].current;
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    card.querySelector('button:not([disabled])')?.focus();
  };
  const open = trips.filter((trip) => !trip.closedAt);
  const gates = data.checkpoints.filter((checkpoint) => checkpoint.type === 'GATE_IN' || checkpoint.type === 'GATE_OUT');
  const myGate = gate ? gates.find((checkpoint) => String(checkpoint.id) === gate) : null;
  const waiting = useMemo(() => callInQueue(data.areas || [], trips, data.vehicles || []).sort((a, b) => b.waitingMs - a.waitingMs), [data.areas, data.vehicles, trips]);
  const overdue = open.filter((trip) => {
    const last = trip.detections?.filter((detection) => !detection.deviation).slice(-1)[0];
    return last && trip.transitMs > 0 && Date.now() - Date.parse(last.detectedAt) > trip.transitMs;
  });
  const arrivals = myGate ? open.filter((trip) => String(trip.checkpoints?.[trip.progress]?.id) === gate) : [];
  return (
    <PageLayout title="Shift Desk" subtitle="Your gate queue, overdue vehicles and shared shift handovers.">
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 3 }}>
        <TextField select label="My gate" value={gate} onChange={(event) => setGate(event.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ minWidth: 260 }}>
          <option value="">Choose a gate</option>
          {gates.map((checkpoint) => <option key={checkpoint.id} value={checkpoint.id}>{`${checkpoint.name} · ${checkpoint.site}`}</option>)}
        </TextField>
        <Chip
          label={`${arrivals.length} due at selected gate`}
          color={arrivals.length ? 'warning' : 'default'}
          clickable={!!arrivals.length}
          onClick={arrivals.length ? () => jump('arrivals') : undefined}
        />
        <Chip
          label={`${overdue.length} overdue stage${overdue.length === 1 ? '' : 's'}`}
          color={overdue.length ? 'error' : 'default'}
          clickable={!!overdue.length}
          onClick={overdue.length ? () => jump('overdue') : undefined}
        />
        <Chip
          label={`${waiting.length} waiting outside`}
          color={waiting.length ? 'warning' : 'default'}
          clickable={!!waiting.length}
          onClick={waiting.length ? () => jump('waiting') : undefined}
        />
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
        <Paper ref={cards.arrivals} variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={700}>Arrivals due next</Typography>
          <StaleNotice updatedAt={updatedAt} unavailable={unavailable} />
          {arrivals.length
            ? arrivals.map((trip) => (
              <Line
                key={trip.id}
                primary={trip.vehicle}
                secondary={`Q-${trip.id} · ${trip.checkpoints?.[trip.progress]?.name || 'Next stage'}`}
                onClick={() => navigate(`/trips/${trip.id}`)}
              />
            ))
            : myGate
              ? <EmptyState updatedAt={updatedAt} unavailable={unavailable} subject="Arrivals" empty={`No trucks are due at ${myGate.name}.`} />
              : <Typography color="text.secondary" sx={{ mt: 1 }}>Choose a gate above to see what is due there.</Typography>}
        </Paper>
        <Paper ref={cards.overdue} variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={700}>Overdue at a stage</Typography>
          <StaleNotice updatedAt={updatedAt} unavailable={unavailable} />
          {overdue.length ? overdue.map((trip) => {
            const last = trip.detections.filter((detection) => !detection.deviation).slice(-1)[0];
            return (
              <Line
                key={trip.id}
                primary={trip.vehicle}
                secondary={`${trip.checkpoints?.[trip.progress]?.name || 'Next stage'} · waiting ${humanMs(minutesSince(last.detectedAt) * 60000)}`}
                onClick={() => navigate(`/trips/${trip.id}`)}
              />
            );
          }) : <EmptyState updatedAt={updatedAt} unavailable={unavailable} subject="Trips" empty="No stage timeouts." />}
        </Paper>
        <Paper ref={cards.waiting} variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={700}>Waiting outside</Typography>
          <StaleNotice updatedAt={updatedAt} unavailable={unavailable} />
          {waiting.length ? waiting.slice(0, 5).map((item) => (
            <Line
              key={`${item.vehicleId}-${item.area}`}
              primary={item.vehicle}
              secondary={`${item.area} · waiting ${humanMs(item.waitingMs)}`}
              // A truck in the parking may have no trip yet — that is what the yard desk is for.
              onClick={() => navigate(item.trip ? `/trips/${item.trip.id}` : '/yard-intake')}
            />
          )) : <EmptyState updatedAt={updatedAt} unavailable={unavailable} subject="The parking feed" empty="No vehicles waiting." />}
        </Paper>
      </Box>
      <HandoverPanel key={gate} gate={gate} updatedAt={updatedAt} />
    </PageLayout>
  );
};

export default ShiftDeskPage;
